/**
 * Invitee Types and Interfaces
 * Based on INVITE_TAB_FLOW_DOCUMENTATION.md
 */

/**
 * Co-Applicant data structure
 */
export interface CoApplicant {
  first_name: string
  last_name: string
  email: string
  mobile_no: string
  relationship_type: 'SPOUSE' | 'SIBLING' | 'CHILD' | 'PARENT' | 'NOT_DIRECTLY_RELATED' | 'CO_SIGNER'
  living_unit: '0' | '1' // 1 = Yes, 0 = No
  is_approved_require: '0' | '1' // 1 = Yes, 0 = No
  income_verified: '0' | '1' // 1 = Verified, 0 = Not Verified
  income?: string
  // Additional fields returned by invite list for co-applicants (Angular parity)
  invite_code?: string
  status?: string
  created_at?: string | number
  invite_status_date?: string | number
}

/**
 * Main Invitee object from API
 */
export interface Invitee {
  ai_id?: string
  invite_code: string
  agent_name?: string
  /** Stage/Dev Yardi may return property staff under different key – use for display fallback */
  property_staff_name?: string
  applicant_name: string
  first_name: string
  last_name: string
  email: string
  mobile_no: string
  property_name: string
  property_id?: string
  company_name?: string
  company_id?: string
  sales_manager?: string
  rent_amount: string
  income?: string
  unit_number?: string
  status: string
  created_at: string | number // Can be Unix timestamp or ISO string
  invite_status_date?: string | number // Can be Unix timestamp or ISO string
  invite_accepted?: '0' | '1' | 0 | 1
  show_action_button?: 0 | 1
  co_applicant?: CoApplicant[]
  // Additional fields
  lead_id?: string
  formos_user?: boolean
  formos_co_user?: boolean
  already_has_invite?: boolean
  invite_data?: Invitee
  primary_invite_data?: Invitee
}

/**
 * Invitee form data structure
 */
export interface InviteeFormData {
  // Section 1: Property Confirmations
  crime: '0' | '1' // 1 = Yes, 0 = No
  approved_by_liberty: '0' | '1' // 1 = Yes, 0 = No
  allow_to_movein: '0' | '1' // 1 = Yes, 0 = No

  // Section 2: Primary Applicant
  first_name: string
  last_name: string
  email: string
  mobile_no: string
  property_id: string
  unit_number?: string
  rent_amount: string
  lease_start_date: string // YYYY-MM-DD format
  lease_duration: string // Default: "12"
  income_verified: '0' | '1' // 1 = Verified, 0 = Not Verified
  income?: string
  income_period?: 'WEEKLY' | 'MONTHLY' | 'BI_MONTHLY' // Default: "MONTHLY"

  // Section 3: Co-Applicants
  co_applicant: CoApplicant[]

  // Optional fields
  popid?: string // Query parameter from URL
}

/**
 * Filter parameters for invitee list
 */
export interface InviteeFilters {
  page?: number
  perPage?: number
  sortBy?: 'asc' | 'desc'
  orderBy?: string
  q?: string // Search text
  org_id?: string
  company_id?: string
  property_id?: string
  inviteStatus?: ('Liberty' | 'Entrata')[]
  fromDt?: string // YYYY-MM-DD
  toDt?: string // YYYY-MM-DD
}

/**
 * API response for invitee list
 */
export interface InviteeListResponse {
  data: Invitee[]
  current: number
  last: number
  total: number
}

/**
 * Property object for dropdowns
 */
export interface Property {
  property_id: string | number // API may return number, but we convert to string
  property_name: string
  listed?: '0' | '1'
  lease_start_lead_days?: number
  [key: string]: unknown
}

/**
 * Organization object
 */
export interface Organization {
  org_id: string
  org_name: string
  [key: string]: unknown
}

/**
 * Company object
 */
export interface Company {
  company_id: string
  company_name: string
  [key: string]: unknown
}

/**
 * Invitee lookup result (duplicate detection)
 */
export interface InviteeLookupResult {
  first_name?: string
  last_name?: string
  email?: string
  mobile_no?: string
  birthdate?: string // YYYY-MM-DD
  ssn?: string
  security_number?: string
}

/**
 * Create invitee request payload
 */
export interface CreateInviteeRequest {
  first_name: string
  last_name: string
  email: string
  mobile_no: string
  property_id: string
  unit_number?: string
  rent_amount: string
  lease_start_date: string // YYYY-MM-DD
  lease_duration: string
  crime: '0' | '1'
  approved_by_liberty: '0' | '1'
  allow_to_movein: '0' | '1'
  income_verified: '0' | '1'
  income?: string
  income_period?: string
  co_applicant?: CoApplicant[]
  popid?: string
  resubmit_invite?: '1' // For auto-resubmit flow
}

/**
 * Create invitee response
 */
export interface CreateInviteeResponse {
  invite_code: string
  lead_id?: string
  formos_user?: boolean
  formos_co_user?: boolean
  already_has_invite?: boolean
  invite_data?: Invitee
  primary_invite_data?: Invitee
}

/**
 * Invite status mapping
 */
export type InviteStatus = 
  | 'INCOMPLETE'
  | 'PENDING'
  | 'APPROVED'
  | 'PROPERTY_APPROVED'
  | 'DENIED'
  | 'EXPIRED'
  | 'VOID'
  | 'AWAITING'
  | 'NOT_STARTED'
  | 'CANCELLED'
  | 'INVITE_SEND'
  | 'User Registered'
  | 'Application Started'
  | 'Application Incomplete'
  | 'Application Complete'
  | 'Application Paid'
  | 'Awaiting External'
  | 'In Progress'

/**
 * Relationship type options
 */
export type RelationshipType = 
  | 'SPOUSE'
  | 'SIBLING'
  | 'CHILD'
  | 'PARENT'
  | 'NOT_DIRECTLY_RELATED'
  | 'CO_SIGNER'

/**
 * Relationship type display mapping
 */
export const RELATIONSHIP_TYPE_LABELS: Record<RelationshipType, string> = {
  SPOUSE: 'Spouse/Partner',
  SIBLING: 'Sibling',
  CHILD: 'Child',
  PARENT: 'Parent',
  NOT_DIRECTLY_RELATED: 'Not Directly Related',
  CO_SIGNER: 'Co-signer',
}

/**
 * Invite status display mapping
 */
export const INVITE_STATUS_LABELS: Record<string, string> = {
  INCOMPLETE: 'Incomplete',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PROPERTY_APPROVED: 'Property Approved',
  DENIED: 'Denied',
  EXPIRED: 'Expired',
  VOID: 'Void',
  AWAITING: 'Awaiting',
  NOT_STARTED: 'Not Started',
  CANCELLED: 'Cancelled',
  INVITE_SEND: 'Invite Send',
  'User Registered': 'User Registered',
  'Application Started': 'Application Started',
  'Application Incomplete': 'Application Incomplete',
  'Application Complete': 'Application Complete',
  'Application Paid': 'Application Paid',
  'Awaiting External': 'Awaiting External',
  'In Progress': 'In Progress',
}
